import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Save, Loader2, Plus, Trash2, Star } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminTestimonials = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchTestimonials();
  }, [navigate]);

  const fetchTestimonials = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/testimonials`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTestimonials(data);
      }
    } catch (error) {
      toast.error('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTestimonial = (index, field, value) => {
    const updated = [...testimonials];
    updated[index] = { ...updated[index], [field]: value };
    setTestimonials(updated);
  };

  const handleAddTestimonial = () => {
    const newId = testimonials.length > 0 ? Math.max(...testimonials.map(t => t.id)) + 1 : 1;
    setTestimonials([...testimonials, {
      id: newId,
      name: '',
      location: '',
      text: '',
      rating: 5
    }]);
  };

  const handleDeleteTestimonial = async (id, index) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${BACKEND_URL}/api/admin/testimonials/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const updated = [...testimonials];
      updated.splice(index, 1);
      setTestimonials(updated);
      toast.success('Testimonial deleted successfully!');
    } catch (error) {
      toast.error('Failed to delete testimonial');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      
      for (const testimonial of testimonials) {
        if (testimonial.name && testimonial.text) { // Only save if required fields are filled
          await fetch(`${BACKEND_URL}/api/admin/testimonials/${testimonial.id}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(testimonial)
          });
        }
      }
      
      toast.success('Testimonials updated successfully!');
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to update testimonials');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="bg-gradient-to-r from-cyan-600 to-emerald-600 text-white p-6 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard">
              <Button variant="outline" size="icon" className="text-white border-white hover:bg-white/10">
                <ArrowLeft className="w-4 h-4" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold">Testimonials Management</h1>
              <p className="text-cyan-100">Add and edit customer testimonials</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleAddTestimonial} variant="outline" className="text-white border-white hover:bg-white/10">
              <Plus className="w-4 h-4 mr-2" />
              Add Testimonial
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-white text-cyan-600 hover:bg-cyan-50">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto p-6 space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {testimonials.map((testimonial, index) => (
            <Card key={testimonial.id}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Testimonial #{testimonial.id}</CardTitle>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleDeleteTestimonial(testimonial.id, index)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Customer Name</Label>
                  <Input
                    value={testimonial.name}
                    onChange={(e) => handleUpdateTestimonial(index, 'name', e.target.value)}
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <Label>Location</Label>
                  <Input
                    value={testimonial.location}
                    onChange={(e) => handleUpdateTestimonial(index, 'location', e.target.value)}
                    placeholder="Neath, Wales"
                  />
                </div>
                <div>
                  <Label>Testimonial Text</Label>
                  <Textarea
                    value={testimonial.text}
                    onChange={(e) => handleUpdateTestimonial(index, 'text', e.target.value)}
                    placeholder="Customer feedback..."
                    rows={4}
                  />
                </div>
                <div>
                  <Label>Rating</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="1"
                      max="5"
                      value={testimonial.rating}
                      onChange={(e) => handleUpdateTestimonial(index, 'rating', parseInt(e.target.value))}
                      className="w-20"
                    />
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`w-5 h-5 ${star <= testimonial.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        
        {testimonials.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">No testimonials yet</p>
            <Button onClick={handleAddTestimonial} className="bg-cyan-600 hover:bg-cyan-700">
              <Plus className="w-4 h-4 mr-2" />
              Add First Testimonial
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTestimonials;
